import { Transform } from 'class-transformer';
import {
	IsArray,
	IsBoolean,
	IsIn,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	Matches,
	MaxLength,
} from 'class-validator';

export const CUSTOM_FIELD_TYPES = [
	'text',
	'textarea',
	'number',
	'currency',
	'date',
	'datetime',
	'boolean',
	'select',
	'multiselect',
	'phone',
	'email',
	'url',
] as const;

export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_ENTITY_TYPES = [
	'client',
	'item',
	'vendor',
	'invoice',
	'order',
	'quote',
	'opportunity',
	'issue',
	'event',
] as const;

export type CustomFieldEntityType = (typeof CUSTOM_FIELD_ENTITY_TYPES)[number];

export function sanitizeFieldKey(input: string): string {
	return input.replace(/[^a-zA-Z0-9_]/g, '');
}

export class CreateCustomFieldDto {
	@IsString()
	@Transform(({ value }) => sanitizeFieldKey(value))
	@Matches(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/, {
		message:
			'Field key must start with a letter and contain only letters, numbers, and underscores (max 60 chars)',
	})
	@MaxLength(60)
	public fieldKey!: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_ENTITY_TYPES)
	public entityType!: CustomFieldEntityType;

	@IsString()
	@MaxLength(255)
	public label!: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_TYPES)
	public fieldType!: CustomFieldType;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public allowedRoles?: Array<number> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;
}

export class UpdateCustomFieldDto {
	@IsString()
	@MaxLength(255)
	@IsOptional()
	public label?: string;

	@IsString()
	@IsIn(CUSTOM_FIELD_TYPES)
	@IsOptional()
	public fieldType?: CustomFieldType;

	@IsBoolean()
	@IsOptional()
	public isActive?: boolean;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsArray()
	@IsNumber({}, { each: true })
	@IsOptional()
	public allowedRoles?: Array<number> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;
}

export interface CustomFieldDefinitionDto {
	id: number;
	fieldKey: string;
	entityType: string;
	label: string;
	fieldType: string;
	sqlColumnType: string;
	isIndexed: boolean;
	isActive: boolean;
	defaultValue: string | null;
	isRequired: boolean;
	validationRules: Record<string, unknown> | null;
	allowedRoles: Array<number> | null;
	sortOrder: number;
	selectOptions: Array<{ value: string; label: string }> | null;
	ownerUserId: number;
	createdDate: Date;
	modifiedDate: Date;
}

export type GetCustomFieldsResponseDto =
	| {
			success: true;
			data: Array<CustomFieldDefinitionDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateCustomFieldResponseDto =
	| {
			success: true;
			data: CustomFieldDefinitionDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteCustomFieldResponseDto =
	| {
			success: true;
			message: string;
	  }
	| {
			success: false;
			error: string;
	  };

export class BulkUpdateCustomFieldDto {
	@IsString()
	@IsIn(CUSTOM_FIELD_ENTITY_TYPES)
	public entityType!: CustomFieldEntityType;

	@IsArray()
	@IsNumber({}, { each: true })
	public entityIds!: Array<number>;

	@IsString()
	@MaxLength(60)
	public fieldKey!: string;

	// Value can be any type depending on the field type
	@IsOptional()
	public value?: unknown;
}

export type BulkUpdateCustomFieldResponseDto =
	| {
			success: true;
			bulkActionId: number;
	  }
	| {
			success: false;
			error: string;
	  };
